import React, { useMemo } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import Papa from 'papaparse';

import { getSyncStatusMeta } from '../../../utils/productSelectionsSync';
import { ACTION_RESULT_EXPORT_FIELDS } from './reportActionResultContract';

const MAX_VISIBLE_ROWS = 200;
const MAX_REASON_LENGTH = 300;

const getActionCopy = (actionType) => {
  if (actionType === 'push_to_vmt') {
    return {
      title: 'Push rows to VMT',
      description: 'Selected report rows are being sent through the modular reporting VMT action.',
      successReason: 'Sent.',
    };
  }
  return {
    title: 'Sync rows to DI',
    description: 'Selected report rows are being synced through the modular reporting DI action.',
    successReason: 'Synced.',
  };
};

const buildCounts = (rows = []) =>
  rows.reduce(
    (counts, row) => {
      if (row?.status && Object.prototype.hasOwnProperty.call(counts, row.status)) {
        counts[row.status] += 1;
      }
      return counts;
    },
    { total: rows.length, queued: 0, sending: 0, success: 0, failed: 0, skipped: 0 }
  );

const sanitizeReason = (reason, { truncate = true } = {}) => {
  const value = String(reason || '').replace(/\s+/g, ' ').trim();
  if (!value) return '';
  return truncate && value.length > MAX_REASON_LENGTH ? `${value.slice(0, MAX_REASON_LENGTH)}...` : value;
};

const resolveReason = (row, successReason, options) => {
  const reason =
    row.reason ||
    (row.status === 'success'
      ? successReason
      : row.status === 'sending'
        ? 'Sending...'
        : row.status === 'queued'
          ? 'Queued.'
          : '');
  return sanitizeReason(reason, options);
};

const buildActionResultCsv = (rows = [], successReason = 'Sent.') =>
  Papa.unparse(
    rows.map((row) =>
      ACTION_RESULT_EXPORT_FIELDS.reduce((entry, [field, label]) => {
        entry[label] = field === 'reason' ? resolveReason(row, successReason, { truncate: false }) : (row?.[field] ?? '');
        return entry;
      }, {})
    ),
    {
      columns: ACTION_RESULT_EXPORT_FIELDS.map(([, label]) => label),
      escapeFormulae: true,
    }
  );

const downloadTextFile = ({ contents, fileName, mimeType }) => {
  if (typeof window === 'undefined' || !window.URL?.createObjectURL) return false;
  const blob = new Blob([contents], { type: mimeType });
  const objectUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = fileName;
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(objectUrl);
  return true;
};

const ReportActionStatusDialog = ({
  open,
  onClose,
  isMobile,
  actionType,
  rows = [],
  inFlight = false,
  recompute,
  onClearSelection,
  onSelectFailedRows,
}) => {
  const copy = getActionCopy(actionType);
  const counts = useMemo(() => buildCounts(rows), [rows]);
  const progressValue = counts.total > 0 ? ((counts.success + counts.failed + counts.skipped) / counts.total) * 100 : 0;
  const hasFailedRows = counts.failed > 0;
  const recomputeFailed = recompute && recompute.ok === false;
  const visibleRows = useMemo(() => {
    if (rows.length <= MAX_VISIBLE_ROWS) return rows;
    const failedOrSkipped = rows.filter((row) => row.status === 'failed' || row.status === 'skipped');
    if (failedOrSkipped.length) return failedOrSkipped.slice(0, MAX_VISIBLE_ROWS);
    return rows.slice(0, MAX_VISIBLE_ROWS);
  }, [rows]);
  const hiddenRowCount = Math.max(rows.length - visibleRows.length, 0);
  const handleDownloadAllResults = () => {
    downloadTextFile({
      contents: buildActionResultCsv(rows, copy.successReason),
      fileName: `report-action-${String(actionType || 'action').replace(/_/g, '-')}-results.csv`,
      mimeType: 'text/csv;charset=utf-8',
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={isMobile}
      fullWidth
      maxWidth="lg"
      data-testid="report-action-status-modal"
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="h6">{copy.title}</Typography>
            <Typography variant="body2" color="text.secondary">
              {copy.description}
            </Typography>
          </Box>
          {isMobile ? (
            <IconButton aria-label="Close report action status modal" onClick={onClose}>
              <CloseIcon />
            </IconButton>
          ) : null}
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', sm: 'center' }}>
            <Chip size="small" variant="outlined" label={`Total: ${counts.total}`} />
            <Chip size="small" variant="outlined" label={`Queued: ${counts.queued}`} />
            <Chip size="small" variant="outlined" label={`Sending: ${counts.sending}`} />
            <Chip size="small" variant="outlined" color="success" label={`Success: ${counts.success}`} />
            <Chip size="small" variant="outlined" color="error" label={`Failed: ${counts.failed}`} />
            <Chip size="small" variant="outlined" color="warning" label={`Skipped: ${counts.skipped}`} />
            {inFlight ? (
              <Stack direction="row" spacing={1} alignItems="center">
                <CircularProgress size={16} />
                <Typography variant="body2" color="text.secondary">
                  Processing report action
                </Typography>
              </Stack>
            ) : null}
          </Stack>
          {counts.total > 0 ? <LinearProgress variant="determinate" value={Math.min(progressValue, 100)} /> : null}
          {recomputeFailed ? (
            <Alert severity="warning">
              Report action completed, but report recompute failed. Use the manual recompute fallback before continuing.
            </Alert>
          ) : null}
          {hiddenRowCount > 0 ? (
            <Alert severity="info">
              Showing {visibleRows.length} of {rows.length} row results. Failed and skipped rows are prioritized for large batches.
            </Alert>
          ) : null}
          <Divider />
          {visibleRows.length === 0 ? (
            <Alert severity="info">No report rows queued for this action.</Alert>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>BAC</TableCell>
                    <TableCell>Program</TableCell>
                    <TableCell>Product</TableCell>
                    <TableCell>Reference / Order Item</TableCell>
                    <TableCell>DI Status</TableCell>
                    <TableCell>DI Selection Date</TableCell>
                    <TableCell>DI Effective Date</TableCell>
                    <TableCell>Result</TableCell>
                    <TableCell>Reason</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visibleRows.map((row) => {
                    const meta = getSyncStatusMeta(row.status);
                    return (
                      <TableRow key={`${row.id}-${row.index}`} data-testid={`report-action-status-row-${row.id}`}>
                        <TableCell>{row.bac}</TableCell>
                        <TableCell>{row.programName}</TableCell>
                        <TableCell>{row.productLabel}</TableCell>
                        <TableCell>{row.referenceId || row.orderItemId}</TableCell>
                        <TableCell>{row.diStatus}</TableCell>
                        <TableCell>{row.diSelectionDate}</TableCell>
                        <TableCell>{row.diEffectiveDate}</TableCell>
                        <TableCell>
                          <Chip size="small" label={meta.label} color={meta.color} variant="outlined" />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {resolveReason(row, copy.successReason)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button
          onClick={() => {
            onClose();
            onClearSelection?.();
          }}
        >
          Clear selections
        </Button>
        {hasFailedRows ? (
          <Button
            variant="contained"
            color="error"
            onClick={() => {
              const failedIds = rows.filter((row) => row.status === 'failed').map((row) => row.id);
              onClose();
              onSelectFailedRows?.(failedIds);
            }}
          >
            Select failed rows
          </Button>
        ) : null}
        {rows.length > 0 ? (
          <Button onClick={handleDownloadAllResults}>
            Download all results CSV
          </Button>
        ) : null}
      </DialogActions>
    </Dialog>
  );
};

export { buildActionResultCsv };

export default ReportActionStatusDialog;
